import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Step } from "../state/wizard";
import { useWizardContext } from "../screens/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const VISIBLE: { step: Step; label: string }[] = [
  { step: "welcome",    label: "Start" },
  { step: "register",   label: "Details" },
  { step: "companions", label: "Companions" },
  { step: "consent",    label: "Consent" },
  { step: "eta",        label: "Arrival" },
];

export function Stepper({ current }: { current: Step }) {
  const { dispatch } = useWizardContext();
  const i = VISIBLE.findIndex((v) => v.step === current);
  if (i < 0) return null;

  const prev = i > 0 ? VISIBLE[i - 1]! : null;
  const next = i < VISIBLE.length - 1 ? VISIBLE[i + 1]! : null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!prev}
          onClick={() => prev && dispatch({ type: "goto", step: prev.step })}
          className="text-xs font-medium"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">{prev?.label ?? "Back"}</span>
          <span className="sm:hidden">Back</span>
        </Button>
        <div className="flex-1 text-center">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Step {i + 1} of {VISIBLE.length}
          </div>
          <div className="mt-0.5 text-sm font-semibold">{VISIBLE[i]!.label}</div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!next}
          onClick={() => next && dispatch({ type: "goto", step: next.step })}
          className="text-xs font-medium"
        >
          <span className="hidden sm:inline">{next?.label ?? "Next"}</span>
          <span className="sm:hidden">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${((i + 1) / VISIBLE.length) * 100}%` }}
        />
      </div>

      <div className="mt-3 flex items-center justify-between gap-1">
        {VISIBLE.map((v, idx) => {
          const reached = idx <= i;
          const isCurrent = idx === i;
          return (
            <button
              key={v.step}
              type="button"
              onClick={() => dispatch({ type: "goto", step: v.step })}
              className="group flex flex-1 flex-col items-center gap-1 cursor-pointer rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Go to ${v.label}`}
              aria-current={isCurrent ? "step" : undefined}
            >
              <span
                className={cn(
                  "h-2.5 w-2.5 rounded-full transition-colors",
                  isCurrent
                    ? "bg-primary ring-2 ring-primary/30"
                    : reached
                    ? "bg-primary/70 group-hover:bg-primary"
                    : "bg-muted-foreground/30 group-hover:bg-muted-foreground/60"
                )}
              />
              <span
                className={cn(
                  "text-[9px] font-medium uppercase tracking-wide transition-colors",
                  reached ? "text-slate-600 group-hover:text-primary" : "text-muted-foreground/60"
                )}
              >
                {v.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
